/**
 * KD-3496 — the per-room store the optional-supplement selects read back from.
 *
 * Rooms still being offered are keyed by `originalIndex`, but two rooms of the
 * same type share one, so a room the user already validated is keyed by its
 * slot instead. That makes the store positional, and positions move: deleting a
 * validated room shifts every slot after it down, and without shifting the keys
 * with them the room that moves up shows — and, on the next tick, books — the
 * supplements of the one that was removed.
 */

const SELECTED_ROOM_KEY_PATTERN = /^selected-(\d+)$/;

/** The store key for the room validated in slot `roomIndex`. */
export const selectedRoomSupplementKey = (roomIndex) => `selected-${roomIndex}`;

/**
 * The store with the entry for `removedIndex` dropped and every later slot
 * shifted down one. Keys that are not slot keys (offered rooms) are untouched.
 */
export const shiftSelectedRoomSupplementKeys = (store, removedIndex) =>
  Object.entries(store ?? {}).reduce((next, [key, value]) => {
    const match = SELECTED_ROOM_KEY_PATTERN.exec(key);
    if (!match) {
      next[key] = value;
      return next;
    }

    const index = Number(match[1]);
    if (index === removedIndex) {
      return next;
    }

    next[index > removedIndex ? selectedRoomSupplementKey(index - 1) : key] =
      value;
    return next;
  }, {});

/**
 * The store with every offered-room entry dropped, the validated slots kept.
 *
 * An offered room is keyed by `originalIndex`, and the next slot's list offers
 * the same room type under that same key. So the ticks of a selection that is
 * over — validated into a slot, or dropped along with the room it was being made
 * for — have to go with it: the pending selection they belonged to is reset
 * elsewhere, and left in the store they come back ticked on the next slot while
 * the price, the payload and the extra-nights note know nothing about them.
 */
export const clearOfferedRoomSupplementKeys = (store) =>
  Object.entries(store ?? {}).reduce((next, [key, value]) => {
    if (SELECTED_ROOM_KEY_PATTERN.test(key)) {
      next[key] = value;
    }
    return next;
  }, {});

/**
 * Whether `possibility` is the room `room` is already being chosen on.
 *
 * The optional supplements ticked below a card belong to THAT room: moving to
 * another one has to drop them, while changing the promotion or the bed
 * configuration stays on the same room and keeps them. Own inventory — the only
 * inventory that sells optional supplements — keeps one `hotelRoomId` across
 * those variants, which is how the promotion select already finds them; a room
 * without one is identified by its name and origin.
 */
export const isSameOfferedRoom = (room, possibility) => {
  if (!room || !possibility) {
    return false;
  }

  const roomId = room?.supplierSpecificInformations?.hotelRoomId;
  const possibilityId = possibility?.supplierSpecificInformations?.hotelRoomId;
  if (roomId && possibilityId) {
    return roomId === possibilityId;
  }

  return (
    room.name === possibility.name &&
    room.supplier === possibility.supplier &&
    room.source === possibility.source
  );
};
